import { MapState, MapStyle, VisState } from '@carto/kepler.gl/reducers'
import { SourceFilters, SpatialFilter } from '@carto/react-redux'
import { BasemapSettings } from 'features/account/types'
import { Branding } from 'features/branding/models/Branding'
import { GetAttributeStatsCategoryOrder } from 'features/builder/api/builder'
import {
  Widget,
  LegendSettings,
  PopupSettings,
  QueryFilters,
  AISettings,
  DynamicTilingMigrationBackup
} from 'features/builder/types'
import { KeplerMapStyle } from 'features/builder/ui/KeplerGl/types'
import { CommonPrivacy, CommonSharingScope, RequestStatus, SortDirection } from 'features/common/types'
import { ProviderType } from 'features/connections/types'
import { Group } from 'features/groups'
import { QueryParametersSimple } from 'features/sql/types'
import { UserRole } from 'features/users'
import { Comment, CommentResponse } from 'features/collaboration/types'

export enum Formats {
  GEOJSON = 'geojson',
  TILEJSON = 'tilejson',
  JSON = 'json',
  RASTER = 'raster'
}

export enum MapType {
  table = 'table',
  query = 'query',
  tileset = 'tileset',
  raster = 'raster'
}

export enum GeomType {
  Points = 'points',
  Lines = 'lines',
  Polygons = 'polygons',
  Unknown = 'unknown'
}

export type MapPrivacy = CommonPrivacy
export const MapPrivacy = { ...CommonPrivacy }

export type SqlParameterItem<ValueType = string> = {
  sqlName: string
  value: ValueType
}

export enum SqlParameterType {
  DateRange = 'DateRange',
  Category = 'Category',
  Numeric = 'Numeric',
  NumericRange = 'NumericRange'
}

export type SqlBaseParameter = {
  id: string
  name: string
  type: SqlParameterType
}

export type SqlDateRangeParameter = SqlBaseParameter & {
  type: SqlParameterType.DateRange

  // start and end values are in ISO-8601 date format, i.e YYYY-MM-DD regardless of how they are presented
  // to user and/or sent to SQL
  start: SqlParameterItem<string>
  end: SqlParameterItem<string>

  // @deprecated We let the user introduce any Date without restrictions
  allowedRange?: [string, string]
}

export type SqlCategoryParameter = SqlBaseParameter & {
  type: SqlParameterType.Category
  item: SqlParameterItem<string[]>
  values: string[]
  orderBy?: GetAttributeStatsCategoryOrder
}

export type NumericParameterCommon = {
  min: number
  max: number
  step?: number
}
export type SqlNumericParameter = SqlBaseParameter &
  NumericParameterCommon & {
    type: SqlParameterType.Numeric
    item: SqlParameterItem<number>
    defaultValue: number
  }

export type SqlNumericRangeParameter = SqlBaseParameter &
  NumericParameterCommon & {
    type: SqlParameterType.NumericRange
    start: SqlParameterItem<number>
    end: SqlParameterItem<number>
  }

export type SqlParameter = SqlDateRangeParameter | SqlCategoryParameter | SqlNumericParameter | SqlNumericRangeParameter

export type SqlParameterDataSource = {
  id: string
  name: string
  color: string
}

export type BoundSqlParameter = SqlParameter & {
  dataSources: SqlParameterDataSource[]
}

export interface MapSettings {
  controlCacheTime: boolean
  cacheTime: number
  exportViewportData: boolean
  exportPDF: boolean
  comments: boolean
  addressSearchBar: boolean
  sqlParameterControls?: boolean
  scrollWheelZoom: boolean
  featureSelectionTool: boolean
  showPerformanceWarnings: boolean
  showDateControl?: boolean
  showMyLocationButton: boolean
  showTitlePublicMap: boolean
  showMeasureDistanceTool: boolean
  basemapsSelector?: boolean
}

export enum MapRightPaneTab {
  Widgets = 'widgets',
  Parameters = 'parameters'
}

export interface BuilderUiPersistenceState {
  // if map description pane is open
  descriptionOpen: boolean
  descriptionPreview: boolean

  // if controls (widgets & sql parameters) is expected to be open
  controlsPaneOpen: boolean

  // which pane (widgets or sql parameters) is currently active
  controlsPaneTab?: MapRightPaneTab

  // whether comments are visible in the map
  commentsVisible?: boolean
}

export interface AmazonBaseMapSettings {
  identityPoolId?: string
  mapName?: string
}

export enum BasemapFormat {
  WMTS = 'wmts',
  TileJson = 'tilejson',
  Raster = 'raster'
}

export interface BasemapStyleSettings {
  url: string
  tileSize?: number
}

export interface Basemap {
  id: string
  name: string
  attribution?: string
  settings: BasemapStyleSettings
  type: BasemapFormat
  thumbnail: string

  // internal
  disabled?: boolean
}

export type CustomBaseMapSettings = {
  amazon?: AmazonBaseMapSettings
  custom?: Basemap // deprecated, TODO: remove before merge
  customStyle?: KeplerMapStyle
}

export type CartoKeplerMapConfigEnvelope = {
  version?: string
  config: CartoKeplerMapConfig
}

export type CartoKeplerMapConfig = {
  mapState: MapState
  mapStyle: MapStyle

  // TODO: write proper type, there are pars in in @deck.gl/carto types.ts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  visState: VisState

  // custom carto props
  widgets?: Widget[]
  filters?: { [sourceId: string]: SourceFilters }
  spatialFilter?: SpatialFilter
  customBaseMaps?: CustomBaseMapSettings
  legendSettings?: LegendSettings
  mapSettings?: MapSettings
  popupSettings?: PopupSettings
  sourcesQueryFilters?: Record<string, QueryFilters> // depreacted
  sqlParameters?: SqlParameter[]
  aiSettings?: AISettings
  uiState?: BuilderUiPersistenceState
  dynamicTilingMigrationBackup?: DynamicTilingMigrationBackup
}

export type Map = {
  id: string
  title?: string
  description?: string
  thumbnailUrl?: string
  ownerPicture?: string
  ownerEmail?: string
  ownerIsCartoUser?: boolean
  views?: number
  tags?: string[]

  keplerMapConfig?: CartoKeplerMapConfigEnvelope
  createdAt?: string
  updatedAt?: string
  accountId?: string
  ownerId?: string
  token?: string
  privacy?: MapPrivacy
  lastPublishedAt?: string
  publishedWithPassword?: boolean
  publicMapPassword?: string
  groups?: Group[]
  users?: { id: string; email: string; role?: UserRole }[]
  collaborative?: boolean
  lockStatus?: MapLockStatus
  sharingScope?: CommonSharingScope
  // only in public map, as we don't have access to these data using mapToken
  branding?: Branding
  basemaps?: {
    settings: BasemapSettings
    customBasemaps: Basemap[]
  }
}

export type MapLockStatus = {
  locked: boolean
  userId?: string
  email?: string
  pictureUrl?: string
}

export interface DemoMap extends Map {
  templateUrl: string
  exists: boolean
  businessVerticals: string[]
}

export interface MapDataset {
  connectionViewerCredentials?: boolean
  id: string
  type: MapType
  source: string
  format?: Formats
  geoColumn?: string | null
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  connectionId?: string
  connectionName?: string
  providerId: ProviderType
  createdAt?: string
  updatedAt?: string
  label?: string
  color?: string
  uniqueIdProperty?: string
  queryParameters?: QueryParametersSimple | null
  queryTemplate?: string | null
  name?: string | null
  spatialIndex?: string | null
  columns?: string[] | null

  /**
   * Indicates if export to bucket is available for connection this dataset belongs to.
   *
   * Only available in datasets returned from public map endpoint.
   */
  exportToBucketAvailable?: boolean

  //
  // extra fields in builder, in-memory only
  //

  /**
   * `accessToken` to be used to fetch this map dataset data.
   *
   * In certain scenarios, we need to use `map.token` to fetch this map dataset data e.g
   *   - public maps,
   *   - viewer mode without viewer credentials or CartoDW connection
   *
   * See `assignTokensToDatasets`.
   */
  accessToken?: string

  runningSince?: number
  runningTo?: number
  jobId?: string
  status?: RequestStatus
  errorMessage?: string

  sourceWorkflowId?: string | null
  sourceWorkflowNodeId?: string | null
}

export interface MapsQueryFilters {
  search?: string
  filter?: MapsFilter
  order?: MapsOrder
  pagination?: {
    size?: number
    page: number
  }
  refresh?: boolean
}

export interface MapsFilter {
  permissions?: MapOwnerFilter
  tags?: string[]
}

export interface RawMapsQueryFilters extends Omit<MapsQueryFilters, 'filter'> {
  filter: RawMapsFilter
}

export interface RawMapsFilter {
  owner?: string
  privacy?: MapPrivacy
  tags?: string[]
}

export interface MapsOrder {
  by: MapFieldToOrderBy
  direction: SortDirection
}

export enum MapFieldToOrderBy {
  Title = 'title',
  UpdatedAt = 'updated_at'
}

export interface MapEmptyCardProps {
  onClick: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  img: string | any
}

export interface MapDetailsRequest {
  id: string
  privacy: MapPrivacy
}

export interface SetMapPrivacyRequest {
  mapId: string
  privacy: MapPrivacy
  publicPassword?: string | null
  groupIds?: string[]
  userIds?: string[]
  sharingScope?: CommonSharingScope
}

export interface DuplicateMapProps {
  mapId: string
}

export enum DuplicateMapType {
  Full = 'fully',
  Partial = 'partially'
}

export interface DuplicateMapResponse {
  status: DuplicateMapType
}
export interface DryRunDuplicateMapResponse {
  status: DuplicateMapType
  restricted_connections?: string[]
}

export interface CreateDemoMapRequest {
  id: string
  url: string
}

export enum MapActions {
  CreateFromTemplate = 'createFromTemplate',
  Duplicate = 'duplicate',
  Delete = 'delete',
  Edit = 'edit',
  Share = 'share',
  RemoveCredentials = 'removeCredentials'
}

export interface MapConfig {
  zoom?: number
  lat?: number
  lng?: number
  pitch?: number
  bearing?: number
  layers?: number[]
  /** Map of `widgetId` (not `sourceId`) to `filterValues`. */
  filters?: Record<string, string[]>
  spatialFilter?: { geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon }
  search?: string
  sqlParameters?: Record<string, string[]>
}

export enum MapOwnerFilter {
  All = 'all',
  Mine = 'mine',
  Shared = 'shared',
  Public = 'public'
}

export interface MapCommentAnchor {
  latitude: number
  longitude: number
  zoom?: number // we need to store this because comment should lead to same view on map
  mapIndex: number
}

export interface MapComment extends Comment {
  mapId: string
  anchor: MapCommentAnchor
}

export interface MapCommentResponse extends CommentResponse {
  mapId: string
}

export interface MapCommentWithResponses extends MapComment {
  responses: MapCommentResponse[]
}

export const isMapCommentResponse = (comment: MapComment | MapCommentResponse): comment is MapCommentResponse => {
  return 'commentId' in comment
}
